import {
  Children,
  type KeyboardEventHandler,
  type ReactElement,
  useCallback,
  useState,
} from 'react'
import { type Option, type OptionProps } from '../Option'
import { useSelectContext } from './useSelectContext'

type Args<T> = {
  items: Array<ReactElement<OptionProps<T>, typeof Option<T>>>
  listRef: React.MutableRefObject<HTMLElement | null>
  setOpen: (isOpen: boolean) => void
}

function getValue<T>(
  items: Array<ReactElement<OptionProps<T>, typeof Option<T>>>,
  index: number,
) {
  return items[index].props.value
}

export function useListNavigation<T>({ listRef, items, setOpen }: Args<T>) {
  const { focused, setFocused, setSelected } = useSelectContext()
  const [focusedIndex, setFocusedIndex] = useState(
    items.findIndex((item) => item.props.value === focused),
  )

  const onKeyDownHandler = useCallback<KeyboardEventHandler<HTMLElement>>(
    (e) => {
      if (!listRef.current) {
        return
      }

      const itemsAsArray = Children.toArray(items) as Array<
        ReactElement<OptionProps<T>, typeof Option<T>>
      >

      const listItems = listRef.current.children
      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault()
          e.stopPropagation()
          let nextItem = focusedIndex + 1

          if (nextItem >= Children.count(items)) {
            nextItem = 0
          }

          setFocusedIndex(nextItem)
          setFocused(getValue(itemsAsArray, nextItem))
          ;(
            listRef.current.querySelector(`[data-focused=true]`)
              ?.nextSibling as HTMLElement
          )?.scrollIntoView({
            block: 'nearest',
          })
          break
        }
        case 'ArrowUp': {
          e.preventDefault()
          e.stopPropagation()
          let nextItem = focusedIndex - 1
          if (nextItem < 0) {
            nextItem = listItems.length - 1
          }

          setFocusedIndex(nextItem)
          setFocused(getValue(itemsAsArray, nextItem))
          ;(
            listRef.current.querySelector(`[data-focused=true]`)
              ?.previousSibling as HTMLElement
          )?.scrollIntoView({
            block: 'nearest',
          })

          break
        }
        case 'Enter': {
          e.preventDefault()
          e.stopPropagation()
          setSelected(getValue(itemsAsArray, focusedIndex))
          break
        }
        case 'Escape': {
          setOpen(false)
        }
      }
    },
    [focusedIndex, items, listRef, setFocused, setOpen, setSelected],
  )

  const onMouseEnterHandler = useCallback(() => {
    setFocused(undefined)
    setFocusedIndex(-1)
  }, [setFocused, setFocusedIndex])

  return { onKeyDown: onKeyDownHandler, onMouseMove: onMouseEnterHandler }
}
